/**
 * src/shell/finance/PayrollManagementDesk.tsx
 *
 * Universal Payroll & HR Management Desk
 * Supports Mesh users and external staff across Basic, Malaysia, and Uganda jurisdictions.
 */

import React, { useState, useEffect } from 'react';
import type {
  PayrollMemberProfile,
  PayrollRun,
  CalculatedPayslip,
  PaymentMethod,
} from '../../plugins/finance/payroll/payroll-contracts.js';
import { PayrollService } from '../../plugins/finance/payroll/payroll-service.js';

export interface PayrollManagementDeskProps {
  readonly activeJurisdiction?: string;
  readonly tenantId?: string;
  readonly legalEntityId?: string;
  readonly payrollService?: PayrollService;
}

export const PayrollManagementDesk: React.FC<PayrollManagementDeskProps> = ({
  activeJurisdiction = 'GLOBAL',
  tenantId = 'tenant-default',
  legalEntityId = 'ent-main',
  payrollService: customService,
}) => {
  const [service] = useState(() => customService || new PayrollService());
  const [activeTab, setActiveTab] = useState<'roster' | 'runs' | 'statutory'>('roster');
  const [employees, setEmployees] = useState<readonly PayrollMemberProfile[]>(() => {
    // Seed initial staff if empty
    const existing = service.getAllEmployees(tenantId);
    if (existing.length === 0) {
      if (activeJurisdiction === 'MY') {
        service.enrollEmployee({
          tenantId,
          legalEntityId,
          legalFullName: 'Ahmad bin Razak',
          nationalIdOrPassport: '880112-14-5541',
          jobTitle: 'Senior Software Engineer',
          department: 'Engineering',
          employmentType: 'full_time',
          status: 'active',
          hireDate: '2025-01-15',
          baseSalaryMinor: 850000, // RM 8,500.00
          currency: 'MYR',
          payFrequency: 'monthly',
          paymentMethod: 'bank_transfer',
          bankName: 'Maybank',
          bankAccountNumber: '514012345678',
          jurisdictionCode: 'MY',
          malaysiaSettings: {
            epfNumber: 'EPF-884129',
            epfRateEmployeePercent: 11,
            epfRateEmployerPercent: 12,
            socsoCategory: 'category_1',
            eisEnrolled: true,
          },
        });
        service.enrollEmployee({
          tenantId,
          legalEntityId,
          legalFullName: 'Siti Nurhaliza binti Tan',
          nationalIdOrPassport: '920405-10-6622',
          jobTitle: 'Product Operations Lead',
          department: 'Operations',
          employmentType: 'full_time',
          status: 'active',
          hireDate: '2025-03-01',
          baseSalaryMinor: 620000, // RM 6,200.00
          currency: 'MYR',
          payFrequency: 'monthly',
          paymentMethod: 'bank_transfer',
          bankName: 'CIMB Bank',
          bankAccountNumber: '8001234567',
          jurisdictionCode: 'MY',
          malaysiaSettings: {
            epfNumber: 'EPF-920405',
            epfRateEmployeePercent: 11,
            epfRateEmployerPercent: 12,
            socsoCategory: 'category_1',
            eisEnrolled: true,
          },
        });
      } else if (activeJurisdiction === 'UG') {
        service.enrollEmployee({
          tenantId,
          legalEntityId,
          legalFullName: 'Moses Okello',
          nationalIdOrPassport: 'CM9002410294J',
          jobTitle: 'Logistics Operations Lead',
          department: 'Logistics',
          employmentType: 'full_time',
          status: 'active',
          hireDate: '2025-02-01',
          baseSalaryMinor: 350000000, // UGX 3,500,000.00
          currency: 'UGX',
          payFrequency: 'monthly',
          paymentMethod: 'mobile_money',
          mobileMoneyNumber: '+256772123456',
          jurisdictionCode: 'UG',
          ugandaSettings: {
            nssfNumber: 'NSSF-UG-882194',
            nssfRateEmployeePercent: 5,
            nssfRateEmployerPercent: 10,
            tinNumber: '1000000042',
          },
        });
      } else {
        service.enrollEmployee({
          tenantId,
          legalEntityId,
          legalFullName: 'Alex Mercer',
          nationalIdOrPassport: 'US-PASS-994182',
          jobTitle: 'Mesh Protocol Core Developer',
          department: 'Engineering',
          employmentType: 'full_time',
          status: 'active',
          hireDate: '2025-01-01',
          baseSalaryMinor: 950000, // $9,500.00
          currency: 'USD',
          payFrequency: 'monthly',
          paymentMethod: 'bank_transfer',
          bankName: 'JPMorgan Chase',
          bankAccountNumber: '987654321',
          jurisdictionCode: 'GLOBAL',
        });
      }
    }
    return service.getAllEmployees(tenantId);
  });

  const [payrollRuns, setPayrollRuns] = useState<readonly PayrollRun[]>(() =>
    service.getAllPayrollRuns(tenantId)
  );

  const [selectedPayslip, setSelectedPayslip] = useState<CalculatedPayslip | null>(null);
  const [editingEmployee, setEditingEmployee] = useState<PayrollMemberProfile | null>(null);
  const [isCreatingEmployee, setIsCreatingEmployee] = useState(false);
  const [isRunningPayroll, setIsRunningPayroll] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // Form State for Employee Editor
  const [formData, setFormData] = useState({
    legalFullName: '',
    nationalIdOrPassport: '',
    meshPrincipalId: '',
    jobTitle: '',
    department: 'Engineering',
    employmentType: 'full_time' as 'full_time' | 'part_time' | 'contractor' | 'intern',
    baseSalary: '5000',
    currency: activeJurisdiction === 'MY' ? 'MYR' : activeJurisdiction === 'UG' ? 'UGX' : 'USD',
    payFrequency: 'monthly' as 'monthly' | 'bi_weekly' | 'weekly',
    paymentMethod: 'bank_transfer' as PaymentMethod,
    bankName: '',
    bankAccountNumber: '',
    mobileMoneyNumber: '',
    // Malaysia specific
    epfNumber: '',
    epfRateEmployeePercent: 11,
    epfRateEmployerPercent: 12,
    socsoCategory: 'category_1' as 'category_1' | 'category_2' | 'exempt',
    eisEnrolled: true,
    // Uganda specific
    nssfNumber: '',
    nssfRateEmployeePercent: 5,
    nssfRateEmployerPercent: 10,
    tinNumber: '',
  });

  const isMalaysia = activeJurisdiction === 'MY';
  const isUganda = activeJurisdiction === 'UG';

  // Keyboard Escape Handler for open dialogs
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setSelectedPayslip(null);
        setEditingEmployee(null);
        setIsCreatingEmployee(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const openCreateEmployee = () => {
    setFormData({
      legalFullName: '',
      nationalIdOrPassport: '',
      meshPrincipalId: '',
      jobTitle: '',
      department: 'Engineering',
      employmentType: 'full_time',
      baseSalary: isUganda ? '3500000' : isMalaysia ? '6000' : '5000',
      currency: isMalaysia ? 'MYR' : isUganda ? 'UGX' : 'USD',
      payFrequency: 'monthly',
      paymentMethod: isUganda ? 'mobile_money' : 'bank_transfer',
      bankName: isMalaysia ? 'Maybank' : 'Bank',
      bankAccountNumber: '',
      mobileMoneyNumber: '+256',
      epfNumber: '',
      epfRateEmployeePercent: 11,
      epfRateEmployerPercent: 12,
      socsoCategory: 'category_1',
      eisEnrolled: true,
      nssfNumber: '',
      nssfRateEmployeePercent: 5,
      nssfRateEmployerPercent: 10,
      tinNumber: '',
    });
    setIsCreatingEmployee(true);
  };

  const openEditEmployee = (emp: PayrollMemberProfile) => {
    setEditingEmployee(emp);
    setFormData({
      legalFullName: emp.legalFullName,
      nationalIdOrPassport: emp.nationalIdOrPassport,
      meshPrincipalId: emp.meshPrincipalId || '',
      jobTitle: emp.jobTitle,
      department: emp.department,
      employmentType: emp.employmentType,
      baseSalary: (emp.baseSalaryMinor / 100).toString(),
      currency: emp.currency,
      payFrequency: emp.payFrequency,
      paymentMethod: emp.paymentMethod,
      bankName: emp.bankName || '',
      bankAccountNumber: emp.bankAccountNumber || '',
      mobileMoneyNumber: emp.mobileMoneyNumber || '',
      epfNumber: emp.malaysiaSettings?.epfNumber || '',
      epfRateEmployeePercent: emp.malaysiaSettings?.epfRateEmployeePercent ?? 11,
      epfRateEmployerPercent: emp.malaysiaSettings?.epfRateEmployerPercent ?? 12,
      socsoCategory: emp.malaysiaSettings?.socsoCategory || 'category_1',
      eisEnrolled: emp.malaysiaSettings?.eisEnrolled ?? true,
      nssfNumber: emp.ugandaSettings?.nssfNumber || '',
      nssfRateEmployeePercent: emp.ugandaSettings?.nssfRateEmployeePercent ?? 5,
      nssfRateEmployerPercent: emp.ugandaSettings?.nssfRateEmployerPercent ?? 10,
      tinNumber: emp.ugandaSettings?.tinNumber || '',
    });
  };

  const handleSaveEmployee = (e: React.FormEvent) => {
    e.preventDefault();
    const baseSalaryMinor = Math.round(parseFloat(formData.baseSalary || '0') * 100);

    if (editingEmployee) {
      // Update
      service.updateEmployee(editingEmployee.employeeId, {
        legalFullName: formData.legalFullName,
        nationalIdOrPassport: formData.nationalIdOrPassport,
        meshPrincipalId: formData.meshPrincipalId || undefined,
        jobTitle: formData.jobTitle,
        department: formData.department,
        employmentType: formData.employmentType,
        baseSalaryMinor,
        currency: formData.currency,
        payFrequency: formData.payFrequency,
        paymentMethod: formData.paymentMethod,
        bankName: formData.paymentMethod === 'bank_transfer' ? formData.bankName : undefined,
        bankAccountNumber: formData.paymentMethod === 'bank_transfer' ? formData.bankAccountNumber : undefined,
        mobileMoneyNumber: formData.paymentMethod === 'mobile_money' ? formData.mobileMoneyNumber : undefined,
        malaysiaSettings: isMalaysia
          ? {
              epfNumber: formData.epfNumber,
              epfRateEmployeePercent: Number(formData.epfRateEmployeePercent),
              epfRateEmployerPercent: Number(formData.epfRateEmployerPercent),
              socsoCategory: formData.socsoCategory,
              eisEnrolled: formData.eisEnrolled,
            }
          : undefined,
        ugandaSettings: isUganda
          ? {
              nssfNumber: formData.nssfNumber,
              nssfRateEmployeePercent: Number(formData.nssfRateEmployeePercent),
              nssfRateEmployerPercent: Number(formData.nssfRateEmployerPercent),
              tinNumber: formData.tinNumber,
            }
          : undefined,
      });
      setNotice(`Updated staff profile for ${formData.legalFullName}.`);
    } else {
      // Create new
      service.enrollEmployee({
        tenantId,
        legalEntityId,
        legalFullName: formData.legalFullName,
        nationalIdOrPassport: formData.nationalIdOrPassport,
        meshPrincipalId: formData.meshPrincipalId || undefined,
        jobTitle: formData.jobTitle,
        department: formData.department,
        employmentType: formData.employmentType,
        status: 'active',
        hireDate: new Date().toISOString().slice(0, 10),
        baseSalaryMinor,
        currency: formData.currency,
        payFrequency: formData.payFrequency,
        paymentMethod: formData.paymentMethod,
        bankName: formData.paymentMethod === 'bank_transfer' ? formData.bankName : undefined,
        bankAccountNumber: formData.paymentMethod === 'bank_transfer' ? formData.bankAccountNumber : undefined,
        mobileMoneyNumber: formData.paymentMethod === 'mobile_money' ? formData.mobileMoneyNumber : undefined,
        jurisdictionCode: activeJurisdiction,
        malaysiaSettings: isMalaysia
          ? {
              epfNumber: formData.epfNumber,
              epfRateEmployeePercent: Number(formData.epfRateEmployeePercent),
              epfRateEmployerPercent: Number(formData.epfRateEmployerPercent),
              socsoCategory: formData.socsoCategory,
              eisEnrolled: formData.eisEnrolled,
            }
          : undefined,
        ugandaSettings: isUganda
          ? {
              nssfNumber: formData.nssfNumber,
              nssfRateEmployeePercent: Number(formData.nssfRateEmployeePercent),
              nssfRateEmployerPercent: Number(formData.nssfRateEmployerPercent),
              tinNumber: formData.tinNumber,
            }
          : undefined,
      });
      setNotice(`Enrolled new employee: ${formData.legalFullName}.`);
    }

    setEmployees(service.getAllEmployees(tenantId));
    setEditingEmployee(null);
    setIsCreatingEmployee(false);
    setTimeout(() => setNotice(null), 4000);
  };

  const handleRunPayroll = async () => {
    setIsRunningPayroll(true);
    try {
      const periodKey = new Date().toISOString().slice(0, 7); // e.g. "2026-08"
      const { payrollRun } = await service.executePayrollRun({
        tenantId,
        legalEntityId,
        periodKey,
        jurisdictionCode: activeJurisdiction,
        approvedBy: 'Admin Principal',
      });
      setPayrollRuns(service.getAllPayrollRuns(tenantId));
      setActiveTab('runs');
      setNotice(`Successfully processed payroll run for ${periodKey} (${payrollRun.payslips.length} payslips).`);
      setTimeout(() => setNotice(null), 4000);
    } catch (err: any) {
      setNotice(`Payroll execution error: ${err.message}`);
    } finally {
      setIsRunningPayroll(false);
    }
  };

  return (
    <div
      className="flex flex-col h-full p-6 space-y-6 font-mono select-none overflow-y-auto"
      style={{
        background: 'var(--theme-bg-app, #090d16)',
        color: 'var(--theme-text-primary, #f8fafc)',
      }}
    >
      {/* Header */}
      <header
        className="flex justify-between items-center rounded-2xl p-6 shadow-xl"
        style={{
          background: 'var(--theme-bg-sidebar, #0f172a)',
          border: '1px solid var(--theme-border, #1e293b)',
        }}
      >
        <div>
          <span className="text-xs uppercase font-bold tracking-widest" style={{ color: 'var(--theme-accent, #38bdf8)' }}>
            {isMalaysia ? 'Malaysia Statutory Payroll (LHDN • EPF • SOCSO)' : isUganda ? 'Uganda Statutory Payroll (URA • NSSF • PAYE)' : 'Global Commercial Payroll'}
          </span>
          <h1 className="text-2xl font-black mt-1" style={{ color: 'var(--theme-text-primary, #ffffff)' }}>
            Sovereign Payroll &amp; HR Desk
          </h1>
          <p className="text-xs" style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}>
            Extended staff profiles, payment configurations &amp; balanced double-entry ledger integration.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div
            className="px-4 py-2 rounded-xl text-center"
            style={{ background: 'var(--theme-bg-card, #090d16)', border: '1px solid var(--theme-border, #1e293b)' }}
          >
            <span className="text-[10px] uppercase font-bold" style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}>Active Staff</span>
            <div className="text-lg font-black text-emerald-400">{employees.length}</div>
          </div>
          <div
            className="px-4 py-2 rounded-xl text-center"
            style={{ background: 'var(--theme-bg-card, #090d16)', border: '1px solid var(--theme-border, #1e293b)' }}
          >
            <span className="text-[10px] uppercase font-bold" style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}>Payroll Runs</span>
            <div className="text-lg font-black text-sky-400">{payrollRuns.length}</div>
          </div>
          <button
            onClick={openCreateEmployee}
            className="px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-lg transition-all flex items-center gap-1"
          >
            <span>+ Enroll Employee</span>
          </button>
          <button
            onClick={handleRunPayroll}
            disabled={isRunningPayroll}
            className="px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-xs shadow-lg transition-all"
          >
            {isRunningPayroll ? 'Calculating...' : '⚡ Execute Monthly Payroll'}
          </button>
        </div>
      </header>

      {/* Notice Banner */}
      {notice && (
        <div
          className="text-xs px-4 py-3 rounded-xl flex items-center justify-between"
          style={{
            background: 'rgba(56, 189, 248, 0.15)',
            border: '1px solid var(--theme-accent, #38bdf8)',
            color: '#38bdf8',
          }}
        >
          <span>✓ {notice}</span>
          <button onClick={() => setNotice(null)} style={{ color: '#38bdf8', fontWeight: 700 }}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 pb-2 text-xs font-bold" style={{ borderBottom: '1px solid var(--theme-border, #1e293b)' }}>
        <button
          onClick={() => setActiveTab('roster')}
          className="px-4 py-2 rounded-xl transition-all font-bold"
          style={{
            background: activeTab === 'roster' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-bg-sidebar, #131c2e)',
            color: activeTab === 'roster' ? '#090d16' : 'var(--theme-text-secondary, #94a3b8)',
            border: '1px solid var(--theme-border, #1e293b)',
          }}
        >
          Staff &amp; Contractor Roster ({employees.length})
        </button>
        <button
          onClick={() => setActiveTab('runs')}
          className="px-4 py-2 rounded-xl transition-all font-bold"
          style={{
            background: activeTab === 'runs' ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-bg-sidebar, #131c2e)',
            color: activeTab === 'runs' ? '#090d16' : 'var(--theme-text-secondary, #94a3b8)',
            border: '1px solid var(--theme-border, #1e293b)',
          }}
        >
          Payroll Runs &amp; Ledger Postings ({payrollRuns.length})
        </button>
      </div>


      {/* Tab: Roster */}
      {activeTab === 'roster' && (
        <div
          className="rounded-2xl p-6 shadow-xl overflow-x-auto"
          style={{
            background: 'var(--theme-bg-card, #0f172a)',
            border: '1px solid var(--theme-border, #1e293b)',
          }}
        >
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--theme-border, #1e293b)', color: 'var(--theme-text-secondary, #94a3b8)' }} className="uppercase text-[10px]">
                <th className="pb-3">Employee Name</th>
                <th className="pb-3">Job Title &amp; Dept</th>
                <th className="pb-3">Type</th>
                <th className="pb-3">Base Salary</th>
                <th className="pb-3">Payment Method</th>
                <th className="pb-3">Statutory Scheme</th>
                <th className="pb-3">Status</th>
                <th className="pb-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--theme-border, #1e293b)' }}>
              {employees.map((emp) => (
                <tr key={emp.employeeId} style={{ borderBottom: '1px solid var(--theme-border, #1e293b)' }}>
                  <td className="py-3 font-bold" style={{ color: 'var(--theme-text-primary, #ffffff)' }}>
                    <div>{emp.legalFullName}</div>
                    <div className="text-[10px] font-mono" style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}>
                      {emp.nationalIdOrPassport} {emp.meshPrincipalId ? `• ${emp.meshPrincipalId}` : ''}
                    </div>
                  </td>
                  <td className="py-3">
                    <div style={{ color: 'var(--theme-text-primary, #f8fafc)' }}>{emp.jobTitle}</div>
                    <div className="text-[10px]" style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}>{emp.department}</div>
                  </td>
                  <td className="py-3 uppercase text-[10px]" style={{ color: 'var(--theme-text-secondary, #94a3b8)' }}>{emp.employmentType.replace('_', ' ')}</td>
                  <td className="py-3 font-bold text-emerald-400">
                    {emp.currency} {(emp.baseSalaryMinor / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="py-3" style={{ color: 'var(--theme-text-primary, #f8fafc)' }}>
                    {emp.paymentMethod === 'bank_transfer'
                      ? `Bank: ${emp.bankName || 'Direct Deposit'} (${emp.bankAccountNumber || '—'})`
                      : emp.paymentMethod === 'mobile_money'
                      ? `MoMo: ${emp.mobileMoneyNumber || '—'}`
                      : 'Cash / Cheque'}
                  </td>
                  <td className="py-3 text-[11px] text-sky-400">
                    {emp.jurisdictionCode === 'MY'
                      ? `EPF (${emp.malaysiaSettings?.epfRateEmployeePercent || 11}%) + SOCSO`
                      : emp.jurisdictionCode === 'UG'
                      ? `NSSF (${emp.ugandaSettings?.nssfRateEmployeePercent || 5}%) + PAYE`
                      : 'Standard Core'}
                  </td>
                  <td className="py-3">
                    <span className="px-2 py-1 bg-emerald-950 text-emerald-400 border border-emerald-800 rounded-lg text-[10px] font-bold">
                      ● Active
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    <button
                      onClick={() => openEditEmployee(emp)}
                      className="px-3 py-1 border rounded-lg text-xs font-bold transition-all"
                      style={{
                        background: 'var(--theme-bg-sidebar, #131c2e)',
                        borderColor: 'var(--theme-accent, #38bdf8)',
                        color: 'var(--theme-accent, #38bdf8)',
                      }}
                    >
                      Configure / Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {/* Tab: Runs */}
      {activeTab === 'runs' && (
        <div className="space-y-4">
          {payrollRuns.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center text-slate-500">
              <div className="text-3xl mb-2">📑</div>
              <div className="font-bold text-sm text-slate-300">No Payroll Runs Executed Yet</div>
              <div className="text-xs mt-1">Click "Execute Monthly Payroll" above to generate this period's run.</div>
            </div>
          ) : (
            payrollRuns.map((run) => (
              <div key={run.payrollRunId} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-4">
                <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                  <div>
                    <span className="text-xs text-sky-400 font-bold">Period: {run.periodKey}</span>
                    <h3 className="text-lg font-black text-white">Payroll Run #{run.payrollRunId.slice(-8).toUpperCase()}</h3>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="px-3 py-1 bg-emerald-950 text-emerald-400 border border-emerald-800 rounded-xl text-xs font-bold">
                      ✓ {run.status.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 text-center">
                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Total Gross Pay</span>
                    <div className="text-base font-black text-white">
                      {(run.totalGrossMinor / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Net Salaries Payable</span>
                    <div className="text-base font-black text-emerald-400">
                      {(run.totalNetMinor / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Employee Statutory</span>
                    <div className="text-base font-black text-amber-400">
                      {(run.totalEmployeeStatutoryMinor / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                    <span className="text-[10px] text-slate-400 uppercase font-bold">Employer Statutory</span>
                    <div className="text-base font-black text-sky-400">
                      {(run.totalEmployerStatutoryMinor / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <div className="text-xs font-bold text-slate-300 mb-2">Individual Payslips ({run.payslips.length}):</div>
                  <div className="grid grid-cols-2 gap-3">
                    {run.payslips.map((ps) => (
                      <div
                        key={ps.payslipId}
                        onClick={() => setSelectedPayslip(ps)}
                        className="bg-slate-950/80 border border-slate-800 hover:border-sky-500 p-4 rounded-xl cursor-pointer transition-all flex justify-between items-center"
                      >
                        <div>
                          <div className="font-bold text-white text-xs">{ps.employeeName}</div>
                          <div className="text-[10px] text-slate-400">
                            Gross: {ps.currency} {(ps.grossSalaryMinor / 100).toFixed(2)} • Net: {ps.currency} {(ps.netSalaryMinor / 100).toFixed(2)}
                          </div>
                        </div>
                        <span className="text-xs text-sky-400 font-bold">Inspect ➔</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Employee Editor Modal */}
      {(isCreatingEmployee || editingEmployee) && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditingEmployee(null);
              setIsCreatingEmployee(false);
            }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md font-mono"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <span className="text-[10px] text-emerald-400 uppercase font-bold">
                  {editingEmployee ? 'Edit Staff Profile' : 'Enroll New Staff Member'}
                </span>
                <h2 className="text-lg font-black text-white">
                  {editingEmployee ? editingEmployee.legalFullName : 'Employee Profile & Payment Configuration'}
                </h2>
              </div>
              <button
                onClick={() => {
                  setEditingEmployee(null);
                  setIsCreatingEmployee(false);
                }}
                className="text-slate-400 hover:text-white font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveEmployee} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-slate-400 block mb-1">Full Legal Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.legalFullName}
                    onChange={(e) => setFormData({ ...formData, legalFullName: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-white"
                    placeholder="e.g. Siti Nurhaliza"
                  />
                </div>
                <div>
                  <label className="text-slate-400 block mb-1">National ID / Passport *</label>
                  <input
                    type="text"
                    required
                    value={formData.nationalIdOrPassport}
                    onChange={(e) => setFormData({ ...formData, nationalIdOrPassport: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-white"
                    placeholder="e.g. 920405-10-6622"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-slate-400 block mb-1">Job Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.jobTitle}
                    onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-white"
                    placeholder="e.g. Lead Engineer"
                  />
                </div>
                <div>
                  <label className="text-slate-400 block mb-1">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-white"
                  />
                </div>
                <div>
                  <label className="text-slate-400 block mb-1">Employment Type</label>
                  <select
                    value={formData.employmentType}
                    onChange={(e) => setFormData({ ...formData, employmentType: e.target.value as any })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-white"
                  >
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contractor">Contractor</option>
                    <option value="intern">Intern</option>
                  </select>
                </div>
              </div>

              {/* Salary & Payment */}
              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-3">
                <div className="text-[10px] text-sky-400 uppercase font-bold">Compensation & Payment Method</div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-slate-400 block mb-1">Base Salary (Monthly)</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.baseSalary}
                      onChange={(e) => setFormData({ ...formData, baseSalary: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-emerald-400 font-bold"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 block mb-1">Currency</label>
                    <input
                      type="text"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value.toUpperCase() })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 block mb-1">Payment Method</label>
                    <select
                      value={formData.paymentMethod}
                      onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                    >
                      <option value="bank_transfer">Bank Transfer (Direct Deposit)</option>
                      <option value="mobile_money">Mobile Money (MoMo / Airtel)</option>
                      <option value="cash">Cash / Physical Voucher</option>
                    </select>
                  </div>
                </div>

                {formData.paymentMethod === 'bank_transfer' && (
                  <div className="grid grid-cols-2 gap-3 pt-2">
                    <div>
                      <label className="text-slate-400 block mb-1">Bank Name</label>
                      <input
                        type="text"
                        value={formData.bankName}
                        onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                        placeholder="e.g. Maybank, Stanbic"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 block mb-1">Account Number</label>
                      <input
                        type="text"
                        value={formData.bankAccountNumber}
                        onChange={(e) => setFormData({ ...formData, bankAccountNumber: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                      />
                    </div>
                  </div>
                )}

                {formData.paymentMethod === 'mobile_money' && (
                  <div className="pt-2">
                    <label className="text-slate-400 block mb-1">Mobile Money Phone Number</label>
                    <input
                      type="text"
                      value={formData.mobileMoneyNumber}
                      onChange={(e) => setFormData({ ...formData, mobileMoneyNumber: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                      placeholder="e.g. +256 772 123 456"
                    />
                  </div>
                )}
              </div>

              {/* Malaysia Statutory */}
              {isMalaysia && (
                <div className="p-4 bg-slate-950 rounded-xl border border-sky-900/50 space-y-3">
                  <div className="text-[10px] text-sky-400 uppercase font-bold">Malaysia Statutory Settings (EPF • SOCSO • EIS)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-slate-400 block mb-1">EPF / KWSP No.</label>
                      <input
                        type="text"
                        value={formData.epfNumber}
                        onChange={(e) => setFormData({ ...formData, epfNumber: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                        placeholder="EPF-123456"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 block mb-1">Employee EPF (%)</label>
                      <input
                        type="number"
                        value={formData.epfRateEmployeePercent}
                        onChange={(e) => setFormData({ ...formData, epfRateEmployeePercent: Number(e.target.value) })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 block mb-1">Employer EPF (%)</label>
                      <input
                        type="number"
                        value={formData.epfRateEmployerPercent}
                        onChange={(e) => setFormData({ ...formData, epfRateEmployerPercent: Number(e.target.value) })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Uganda Statutory */}
              {isUganda && (
                <div className="p-4 bg-slate-950 rounded-xl border border-amber-900/50 space-y-3">
                  <div className="text-[10px] text-amber-400 uppercase font-bold">Uganda Statutory Settings (NSSF • URA TIN)</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-slate-400 block mb-1">NSSF Number</label>
                      <input
                        type="text"
                        value={formData.nssfNumber}
                        onChange={(e) => setFormData({ ...formData, nssfNumber: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                        placeholder="NSSF-UG-123456"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 block mb-1">Employee NSSF (%)</label>
                      <input
                        type="number"
                        value={formData.nssfRateEmployeePercent}
                        onChange={(e) => setFormData({ ...formData, nssfRateEmployeePercent: Number(e.target.value) })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-slate-400 block mb-1">URA TIN Number</label>
                      <input
                        type="text"
                        value={formData.tinNumber}
                        onChange={(e) => setFormData({ ...formData, tinNumber: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white"
                        placeholder="1000000000"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setEditingEmployee(null);
                    setIsCreatingEmployee(false);
                  }}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-lg"
                >
                  {editingEmployee ? 'Save Profile Changes' : 'Enroll Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payslip Modal */}
      {selectedPayslip && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedPayslip(null);
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
        >
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <div>
                <span className="text-[10px] text-sky-400 uppercase font-bold">Payslip Summary</span>
                <h2 className="text-lg font-black text-white">{selectedPayslip.employeeName}</h2>
                <div className="text-xs text-slate-400">Period: {selectedPayslip.periodKey}</div>
              </div>
              <button onClick={() => setSelectedPayslip(null)} className="text-slate-400 hover:text-white font-bold">✕</button>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-slate-800">
                <span className="text-slate-400">Base Salary</span>
                <span className="font-bold text-white">{selectedPayslip.currency} {(selectedPayslip.baseSalaryMinor / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-800">
                <span className="text-slate-400">Gross Salary</span>
                <span className="font-bold text-white">{selectedPayslip.currency} {(selectedPayslip.grossSalaryMinor / 100).toFixed(2)}</span>
              </div>

              {selectedPayslip.statutoryBreakdown.map((item) => (
                <div key={item.code} className="flex justify-between py-1 border-b border-slate-800/60 text-slate-300">
                  <span>{item.label} (Employee)</span>
                  <span className="text-rose-400 font-bold">- {selectedPayslip.currency} {(item.employeeMinor / 100).toFixed(2)}</span>
                </div>
              ))}

              <div className="flex justify-between py-3 border-t-2 border-slate-700 text-sm font-black text-emerald-400">
                <span>Net Salary Payable</span>
                <span>{selectedPayslip.currency} {(selectedPayslip.netSalaryMinor / 100).toFixed(2)}</span>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setSelectedPayslip(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
